/**
 * PortalKnowledgeBasePage — Base de Conhecimento no Portal (E21.2.3, FR-15).
 *
 * O parceiro navega os Artigos de Conhecimento PUBLICADOS da sua Audiência
 * (resolvido server-side), busca por texto (full-text PT-BR, ordenado por
 * relevância), filtra por categoria e abre o leitor pra ler o conteúdo.
 * 4 estados: loading / erro / vazio / preenchido.
 */
import { useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Search,
} from "lucide-react";

import { usePortalArticles } from "@/hooks/usePortalCentralFeed";
import type { PortalArticleListItem } from "@/services/portalApi";
import { PortalArticleReaderDialog } from "@/components/portal/PortalArticleReaderDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

function formatDate(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function PortalKnowledgeBasePage() {
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const pageSize = 20;

  // Debounce da busca: não dispara FTS a cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isError, refetch, isFetching } = usePortalArticles(
    page,
    pageSize,
    category.trim() || undefined,
    q || undefined
  );

  const [openId, setOpenId] = useState<string | null>(null);
  const [openTitle, setOpenTitle] = useState<string | undefined>(undefined);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (data && page > totalPages) setPage(totalPages);
  }, [data, page, totalPages]);
  // Volta pra página 1 quando muda a busca ou a categoria.
  useEffect(() => {
    setPage(1);
  }, [q, category]);

  const openReader = (a: PortalArticleListItem) => {
    setOpenId(a.id);
    setOpenTitle(a.title);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex items-center gap-2">
        <BookOpen className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Base de Conhecimento</h1>
          <p className="text-muted-foreground">
            Artigos de orientação de processo. Busque, navegue e leia.
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar nos artigos…"
            className="pl-8"
            data-testid="portal-article-search"
          />
        </div>
        <Input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="Filtrar por categoria…"
          className="max-w-xs"
          data-testid="portal-article-category-filter"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="py-4">
                <Skeleton className="h-5 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar a Base de Conhecimento</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            Não foi possível carregar os artigos.
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      ) : items.length === 0 ? (
        <div className="py-16 text-center" data-testid="portal-article-empty">
          <BookOpen className="mx-auto mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="text-muted-foreground">
            {q || category.trim()
              ? "Nenhum artigo encontrado para a sua busca."
              : "Nenhum artigo disponível por enquanto."}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3" data-testid="portal-article-list">
            {items.map((a) => (
              <Card
                key={a.id}
                className="cursor-pointer transition-colors hover:border-primary/50"
                data-testid="portal-article-card"
                onClick={() => openReader(a)}
              >
                <CardContent className="flex items-center justify-between gap-3 py-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-base font-semibold">{a.title}</h2>
                      <Badge variant="secondary">{a.category}</Badge>
                      {a.is_mandatory && (
                        <Badge
                          variant={a.confirmed ? "outline" : "destructive"}
                          className="gap-1"
                          data-testid={`portal-article-mandatory-${a.id}`}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          Leitura obrigatória
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {a.sector_name && <span>{a.sector_name}</span>}
                      {a.sector_name && a.published_at && <span>•</span>}
                      {a.published_at && <span>{formatDate(a.published_at)}</span>}
                      {a.is_mandatory && a.confirmed && (
                        <>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1 font-medium text-green-600 dark:text-green-500">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Confirmada
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      openReader(a);
                    }}
                    data-testid={`portal-article-read-${a.id}`}
                  >
                    Ler
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                Página {page} de {totalPages} ({total} artigos)
                {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {openId && (
        <PortalArticleReaderDialog
          articleId={openId}
          fallbackTitle={openTitle}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}
